import React, { useEffect, useState } from 'react'
import { Toast, ProgressBar } from 'react-bootstrap'
import {
  subscribeExportProgress,
  ExportToastState,
  publishExportProgress,
  hideExportProgress,
  requestExportCancel,
} from '../utils/exportProgressBus'
import { X, Minimize2, Maximize2, Square } from 'lucide-react'

const ExportProgressToast: React.FC = () => {
  const [state, setState] = useState<ExportToastState>({
    visible: false,
    phase: 'preparing',
    overallTotal: 0,
    overallDone: 0,
    imageTotal: 0,
    imageDone: 0,
    perTag: {},
    minimized: true,
  })

  useEffect(() => {
    const unsub = subscribeExportProgress(setState)
    return () => unsub()
  }, [])

  if (!state.visible) return null

  const overallPct =
    state.overallTotal > 0 ? Math.round((state.overallDone / state.overallTotal) * 100) : 0
  const imagePct =
    state.imageTotal && state.imageTotal > 0
      ? Math.round(((state.imageDone || 0) / state.imageTotal) * 100)
      : 0
  const title =
    state.phase === 'creating'
      ? 'Creating project'
      : state.phase === 'preparing'
      ? 'Preparing images and labels'
      : state.phase === 'uploading'
      ? 'Uploading'
      : state.phase === 'success'
      ? 'Export complete'
      : 'Export failed'

  const perTagEntries = Object.entries(state.perTag || {}).sort((a, b) => {
    const aggA = a[1]
    const aggB = b[1]
    const totalA = aggA.total || 0
    const totalB = aggB.total || 0
    const doneA = aggA.done || 0
    const doneB = aggB.done || 0
    const completeA = totalA > 0 && doneA >= totalA
    const completeB = totalB > 0 && doneB >= totalB
    if (completeA !== completeB) return completeA ? 1 : -1 // completed go to bottom
    const pctA = totalA > 0 ? doneA / totalA : 0
    const pctB = totalB > 0 ? doneB / totalB : 0
    return pctB - pctA // descending among non-complete
  })

  const toggleMinimize = () => publishExportProgress({ minimized: !state.minimized })
  const canClose = state.phase === 'success' || state.phase === 'error'
  const handleClose = () => hideExportProgress()
  const handleStop = () => requestExportCancel()

  return (
    <div style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1056, maxWidth: 360 }}>
      <Toast bg="dark" show={state.visible}>
        <Toast.Header closeButton={false}>
          <strong className="me-auto">{title}</strong>
          <div className="ms-auto d-flex align-items-center" style={{ gap: 8 }}>
            {!canClose && (
              <button
                type="button"
                onClick={handleStop}
                title="Stop"
                aria-label="Stop"
                className="btn p-0 border-0 bg-transparent text-dark"
              >
                <Square size={18} />
              </button>
            )}
            <button
              type="button"
              onClick={toggleMinimize}
              title={state.minimized ? 'Expand' : 'Minimize'}
              aria-label={state.minimized ? 'Expand' : 'Minimize'}
              className="btn p-0 border-0 bg-transparent text-dark"
            >
              {state.minimized ? <Maximize2 size={18} /> : <Minimize2 size={18} />}
            </button>
            {canClose && (
              <button
                type="button"
                onClick={handleClose}
                title="Close"
                aria-label="Close"
                className="btn p-0 border-0 bg-transparent text-dark"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </Toast.Header>
        <Toast.Body className="text-white">
          <div className="mb-2 small">
            Images: {state.imageDone}/{state.imageTotal} ({imagePct}%)
          </div>
          <ProgressBar
            now={imagePct}
            className="mb-2"
            animated={state.phase !== 'success' && state.phase !== 'error'}
          />

          <div className="mb-2 small">
            Snippets: {state.overallDone}/{state.overallTotal} ({overallPct}%)
          </div>
          <ProgressBar
            now={overallPct}
            className="mb-2"
            animated={state.phase !== 'success' && state.phase !== 'error'}
          />

          {!state.minimized && perTagEntries.length > 0 && (
            <div style={{ maxHeight: 240, overflowY: 'auto' }}>
              {perTagEntries.map(([name, agg]) => {
                const pct = agg.total > 0 ? Math.round((agg.done / agg.total) * 100) : 0
                const barStyle = agg.color ? { backgroundColor: agg.color, height: 8 } : undefined
                return (
                  <div key={name} className="mb-2">
                    <div className="d-flex justify-content-between small">
                      <span>{name}</span>
                      <span>
                        {agg.done}/{agg.total} ({pct}%)
                      </span>
                    </div>
                    {agg.color ? (
                      <div className="progress" style={{ height: 8 }}>
                        <div
                          className="progress-bar"
                          role="progressbar"
                          style={{ width: `${pct}%`, ...barStyle }}
                        />
                      </div>
                    ) : (
                      <ProgressBar
                        now={pct}
                        variant="info"
                        animated={state.phase === 'uploading'}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {state.error && <div className="mt-2 text-danger small">{state.error}</div>}
        </Toast.Body>
      </Toast>
    </div>
  )
}

export default ExportProgressToast
